/**
 * White-label brand configuration.
 *
 * Controls brand name, logo, colors, and labels across the entire widget.
 * Switch brands via VITE_BRAND env var or URL param ?brand=indigene.
 *
 * Adding a new brand:
 * 1. Add logo to public/assets/
 * 2. Add a BrandConfig entry to BRANDS below
 * 3. Set VITE_BRAND=<key> or use ?brand=<key>
 */

// ---------------------------------------------------------------------------
// Brand config interface
// ---------------------------------------------------------------------------

export interface BrandConfig {
  /** Brand key (used in env var and URL param) */
  key: string;
  /** Display name shown in UI */
  name: string;
  /** Short assistant name shown inline in chat (message label, composer
   *  placeholder). Kept separate from `name` so long white-label names
   *  don't bloat inline copy like "… · 14:13". */
  assistantName: string;
  /** Subtitle on login screen */
  subtitle: string;
  /** Path to horizontal logo (for headers) */
  logoHorizontal: string;
  /** Path to primary/stacked logo (for login screen) */
  logoPrimary: string;
  /** Primary brand color (headings, accents) */
  colorPrimary: string;
  /** Light tint of primary (active backgrounds) */
  colorPrimaryLight: string;
  /** Mid-tone purple for interactive elements (chat bubbles, chips) */
  colorPrimaryMid: string;
  /** Secondary/accent color (success states, highlights) */
  colorAccent: string;
  /** Light tint of accent */
  colorAccentLight: string;
  /** Spinner/loading label */
  thinkingLabel: string;
  /** Word Add-in manifest display name */
  manifestName: string;
  /** Square brand mark shown beside ``wordmark`` in the panel header.
   *  Brands without one (logo-only white labels) render ``logoHorizontal``. */
  logoMark?: string;
  /** Uppercase wordmark set in the display face next to ``logoMark``. */
  wordmark?: string;
}

// ---------------------------------------------------------------------------
// Brand definitions
// ---------------------------------------------------------------------------

const BRANDS: Record<string, BrandConfig> = {
  asthra: {
    key: "asthra",
    name: "Asthra AI",
    assistantName: "Asthra",
    subtitle: "Regulatory Writing Co-Pilot",
    logoHorizontal: "/assets/asthra-logo-horizontal.png",
    logoPrimary: "/assets/asthra-logo-primary.png",
    // Asthra design system brand (tokens V1.1, Oct 2026): the add-in screen
    // designs' violet. White text on it is 10.6:1.
    colorPrimary: "#5505A8",
    colorPrimaryLight: "#EFE7FB",
    colorPrimaryMid: "#8A4FE0",
    colorAccent: "#52C41A", // Legacy green
    colorAccentLight: "#C2F8CB",
    thinkingLabel: "Asthra is thinking...",
    manifestName: "Asthra Widget",
    logoMark: "/assets/icon_64X64.png",
    wordmark: "ASTHRA",
  },
  indigene: {
    key: "indigene",
    name: "INDEGENE - NEXT MEDTECH DOC SUITE",
    assistantName: "NEXT",
    subtitle: "Regulatory Document Automation",
    logoHorizontal: "/assets/indigene-logo.png",
    logoPrimary: "/assets/indigene-logo.png",
    colorPrimary: "#0067B1", // Indegene blue
    colorPrimaryLight: "#EBF4FB",
    colorPrimaryMid: "#4A9ED6", // Indegene mid blue
    colorAccent: "#E5007D", // Indegene magenta
    colorAccentLight: "#FDE8F2",
    thinkingLabel: "Processing...",
    manifestName: "INDEGENE - NEXT MEDTECH DOC SUITE",
  },
};

// ---------------------------------------------------------------------------
// Brand resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the active brand from env var or URL param.
 *
 * Priority:
 * 1. URL param ?brand=indigene
 * 2. VITE_BRAND env var
 * 3. Default: asthra
 */
function resolveBrand(): BrandConfig {
  // Check URL param
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const urlBrand = params.get("brand");
    if (urlBrand && BRANDS[urlBrand.toLowerCase()]) {
      return BRANDS[urlBrand.toLowerCase()];
    }
  }

  // Check env var
  const envBrand = import.meta.env.VITE_BRAND;
  if (envBrand && BRANDS[envBrand.toLowerCase()]) {
    return BRANDS[envBrand.toLowerCase()];
  }

  return BRANDS.asthra;
}

/** Active brand config — resolved once at module load */
export const brand: BrandConfig = resolveBrand();

/** All available brands (for settings UI if needed) */
export const availableBrands = Object.values(BRANDS);
